import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    Modal,
    Radio,
    RadioGroup,
    TextField,
    Typography,
    CircularProgress,
    Alert,
    Chip,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import CloseIcon from "@mui/icons-material/Close";
import SmsIcon from "@mui/icons-material/Sms";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SendIcon from "@mui/icons-material/Send";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import translate from "../../../i18n/translate";
import ApiRequest from "../../../actions/ApiRequest";
import Notiflix from "notiflix";

const useStyles = makeStyles({
    cardContainer: {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "90%",
        maxWidth: "500px",
        maxHeight: "90vh",
        overflow: "auto",
        outline: "none",
        borderRadius: "12px",
    },
    cardHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderBottom: "1px solid #e0e0e0",
        padding: "16px 20px",
    },
    channelOption: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "12px",
        borderRadius: "8px",
        cursor: "pointer",
        transition: "all 0.2s",
        border: "2px solid transparent",
        "&:hover": {
            backgroundColor: "rgba(25, 118, 210, 0.05)",
        },
    },
    channelSelected: {
        border: "2px solid #1976d2",
        backgroundColor: "rgba(25, 118, 210, 0.1)",
    },
    sellerInfoBox: {
        backgroundColor: "#f5f5f5",
        borderRadius: "8px",
        padding: "12px",
        marginBottom: "16px",
    },
});

const CHANNELS = [
    { value: "sms", label: "SMS", icon: <SmsIcon />, color: "#4caf50" },
    { value: "whatsapp", label: "WhatsApp", icon: <WhatsAppIcon />, color: "#25D366" },
    { value: "web", label: "Web Notification", icon: <NotificationsIcon />, color: "#ff9800" },
];

function ContactSellerModalComponent({ sellerId, sellerName, onClose }) {
    const classes = useStyles();
    const [channel, setChannel] = useState("whatsapp");
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [sellerInfo, setSellerInfo] = useState(null);
    const [loadingInfo, setLoadingInfo] = useState(true);
    const [error, setError] = useState(null);
    const [sendResult, setSendResult] = useState(null);

    // Fetch seller mobile number
    useEffect(() => {
        const fetchSellerInfo = async () => {
            try {
                setLoadingInfo(true);
                const response = await ApiRequest.get(`seller_mobile?seller_id=${sellerId}`);
                if (response?.data?.status === "success") {
                    setSellerInfo(response.data);
                } else {
                    setError(response?.data?.reason || "Failed to fetch seller info");
                }
            } catch (err) {
                setError(err.message || "Error fetching seller info");
            } finally {
                setLoadingInfo(false);
            }
        };

        if (sellerId) {
            fetchSellerInfo();
        }
    }, [sellerId]);

    const handleCopyMobile = useCallback(() => {
        if (sellerInfo?.mobile_number) {
            navigator.clipboard.writeText(sellerInfo.mobile_number);
            Notiflix.Notify.success("Mobile number copied!");
        }
    }, [sellerInfo]);

    const handleSend = useCallback(async () => {
        if (!message.trim()) {
            Notiflix.Notify.warning("Please enter a message");
            return;
        }

        try {
            setLoading(true);
            setSendResult(null);

            const params = new URLSearchParams({
                seller_id: sellerId,
                channel: channel,
                message: message,
            });

            const response = await ApiRequest.post(`contact_seller?${params.toString()}`);

            if (response?.data?.status === "success") {
                setSendResult(response.data.results);
                Notiflix.Notify.success("Message sent successfully!");
                setTimeout(() => onClose(), 2000);
            } else {
                Notiflix.Notify.failure(response?.data?.reason || "Failed to send message");
            }
        } catch (err) {
            Notiflix.Notify.failure(err.message || "Error sending message");
        } finally {
            setLoading(false);
        }
    }, [sellerId, channel, message, onClose]);

    const renderChannelOptions = () => (
        <Grid container spacing={1} sx={{ mb: 2 }}>
            {CHANNELS.map((ch) => (
                <Grid item xs={4} key={ch.value}>
                    <Box
                        className={`${classes.channelOption} ${channel === ch.value ? classes.channelSelected : ""}`}
                        onClick={() => setChannel(ch.value)}
                    >
                        <Box sx={{ color: ch.color, mb: 0.5 }}>{ch.icon}</Box>
                        <Typography variant="caption" sx={{ fontWeight: channel === ch.value ? "bold" : "normal", textAlign: "center" }}>
                            {ch.label}
                        </Typography>
                    </Box>
                </Grid>
            ))}
        </Grid>
    );

    const renderSellerInfo = () => (
        <Box className={classes.sellerInfoBox}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Sending to:
            </Typography>
            <Grid container alignItems="center" spacing={1}>
                <Grid item xs={12}>
                    <Typography variant="body1" fontWeight="bold">
                        {sellerInfo?.name || sellerName} (ID: {sellerId})
                    </Typography>
                </Grid>
                {sellerInfo?.mobile_number && (
                    <Grid item xs={12}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Chip
                                label={sellerInfo.mobile_number}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                            <IconButton size="small" onClick={handleCopyMobile} title="Copy mobile number">
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Grid>
                )}
                {sellerInfo?.email && (
                    <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">
                            Email: {sellerInfo.email}
                        </Typography>
                    </Grid>
                )}
            </Grid>
        </Box>
    );

    return (
        <Modal open={true} onClose={onClose}>
            <Card className={classes.cardContainer}>
                <Box className={classes.cardHeader}>
                    <Typography variant="h6" fontWeight="bold">
                        {translate("Send Notification")}
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
                <CardContent>
                    {loadingInfo ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    ) : (
                        <>
                            {renderSellerInfo()}

                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                Notification Channel
                            </Typography>
                            {renderChannelOptions()}



                            <TextField
                                fullWidth
                                label={translate("Message")}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                multiline
                                rows={4}
                                required
                                sx={{ mb: 2 }}
                            />

                            {sendResult && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    Results: {Object.entries(sendResult)
                                        .filter(([_, v]) => v)
                                        .map(([k, v]) => `${k}: ${v}`)
                                        .join(", ")}
                                </Alert>
                            )}

                            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                                <Button variant="outlined" onClick={onClose} disabled={loading}>
                                    {translate("Cancel")}
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleSend}
                                    disabled={loading || !message.trim()}
                                    startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                                >
                                    {loading ? "Sending..." : translate("Send")}
                                </Button>
                            </Box>

                            <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: "block", textAlign: "center" }}>
                                Sending to 1 selected seller(s) via {CHANNELS.find(c => c.value === channel)?.label}
                            </Typography>
                        </>
                    )}
                </CardContent>
            </Card>
        </Modal>
    );
}

ContactSellerModalComponent.propTypes = {
    sellerId: PropTypes.string.isRequired,
    sellerName: PropTypes.string,
    onClose: PropTypes.func.isRequired,
};

export default function ContactSellerModal({ open, sellerId, sellerName, onClose }) {
    if (!open || !sellerId) {
        return null;
    }
    return <ContactSellerModalComponent sellerId={sellerId} sellerName={sellerName} onClose={onClose} />;
}

ContactSellerModal.propTypes = {
    open: PropTypes.bool,
    sellerId: PropTypes.string,
    sellerName: PropTypes.string,
    onClose: PropTypes.func,
};
